import React from "react";
import { IFolderSelection } from "../folder-selection/folder-navigation";
import { IFolderSelectionSettings } from "../folder-selection/use-folder-selection";
import { IconButton, Text, mergeStyleSets, nuOrderTheme as theme, Checkbox } from "@nuorder/components";
import { formatMessage } from "lib/globalize-util";
import { IFolderNavigationBreadcrumbProps } from "../folder-selection/folder-navigation-breadcrumb";
import { SHARED_WITH_ME_ROOT_ITEM_ID } from "containers/folders/utils/constants";
import { DATATEST } from "constants/integration";

interface IOwnProps extends IFolderNavigationBreadcrumbProps {
    itemLimit: number;
    settings: IFolderSelectionSettings;
    onClose: (event: any) => void;
    onChangeSelection?: (target: IFolderSelection[]) => void;
    onUpdateSettings: (settings: Partial<IFolderSelectionSettings>) => void;
    onSetMaxItemsAlertOpened?: (value: boolean) => void;
}

export const FolderNavigationBreadcrumb: React.FC<IOwnProps> = ({
    breadcrumb,
    totalItems,
    validItems,
    isSellectAllDisabled,
    isFetchingData,
    canFetchMore,
    onClose,
    settings,
    onUpdateSettings,
    itemLimit,
    onChangeSelection,
    onSetMaxItemsAlertOpened,
}) => {
    const classes = React.useMemo(
        () =>
            mergeStyleSets({
                header: {
                    height: 40,
                    display: "flex",
                    backgroundColor: theme.palette.neutralLighter,
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "0px 12px",
                    borderBottom: `1px solid ${theme.palette.neutralLight}`,
                },
                selectAll: {
                    padding: "0px 12px",
                    height: 33,
                    borderBottom: `1px solid ${theme.palette.neutralLight}`,
                    display: "flex",
                    alignItems: "center",
                },
                title: {
                    flex: 1,
                    fontWeight: "bold",
                    margin: 0,
                },
            }),
        []
    );

    const handleSelectAll = React.useCallback(
        (data: IFolderSelection[], canFetchMore: boolean) =>
            (ev: React.FormEvent<HTMLElement | HTMLInputElement>, checked: boolean) => {
                const newItemsToSelect = data.filter(
                    (it) => !(settings.itemSelected as IFolderSelection[]).some((x) => x.id === it.id)
                );
                const selectedItems = checked
                    ? (settings.itemSelected as IFolderSelection[])
                    : (settings.itemSelected as IFolderSelection[]).filter((it) => {
                          if (settings.currentItemId === SHARED_WITH_ME_ROOT_ITEM_ID) {
                              return !it.isSharedRoot;
                          }
                          return it.parent !== settings.currentItemId;
                      });

                let displayAlert = false;
                const newItemsSelected = checked
                    ? newItemsToSelect.reduce(
                          (acc, item) => {
                              if (acc.length <= itemLimit - 1) {
                                  acc.push(item);
                              } else {
                                  displayAlert = true;
                              }

                              return acc;
                          },
                          [...selectedItems]
                      )
                    : selectedItems;

                onUpdateSettings({
                    markAllAsSelected: checked,
                    itemSelected: newItemsSelected,
                    automaticLoad: checked ? !!canFetchMore : false,
                });

                onChangeSelection(newItemsSelected);

                displayAlert && onSetMaxItemsAlertOpened(true);
            },
        [onUpdateSettings, onChangeSelection, settings, itemLimit, onSetMaxItemsAlertOpened]
    );

    const items = [...breadcrumb];
    const displayBackButton = items.length > 1;
    const { text } = items.pop() || { text: formatMessage("Home") };
    const parentItem = displayBackButton && items.pop();
    const handleOnBack = () => {
        if (parentItem) {
            parentItem.onClick(null, parentItem);
        }
    };

    const itemsSelectedLength = (settings.itemSelected as IFolderSelection[]).filter((it) => {
        if (settings.currentItemId === SHARED_WITH_ME_ROOT_ITEM_ID) {
            return it.isSharedRoot;
        }
        return it.parent === settings.currentItemId;
    }).length;
    const markCheckAll = totalItems && totalItems === itemsSelectedLength;

    return (
        <>
            <div className={classes.header}>
                {displayBackButton && <IconButton iconProps={{ iconName: "arrowLeft24" }} onClick={handleOnBack} />}
                <Text variant="medium" nowrap as="h1" className={classes.title}>
                    {text}
                </Text>
                <IconButton iconProps={{ iconName: "close24" }} onClick={onClose} />
            </div>
            <div className={classes.selectAll} data-test={DATATEST.FOLDERS.ITEM_PICKER.SELECT_ALL}>
                <Checkbox
                    label={formatMessage("All")}
                    styles={{ root: { marginTop: 6 } }}
                    disabled={!displayBackButton || isFetchingData || isSellectAllDisabled}
                    checked={markCheckAll}
                    indeterminate={markCheckAll ? false : itemsSelectedLength > 0}
                    onChange={handleSelectAll(validItems, canFetchMore)}
                />
            </div>
        </>
    );
};
